import { UploadProvider } from "@car/ui/components/file-upload";
import { ImageConfigProvider } from "@car/ui/components/image";
import { registerServiceWorker } from "@car/ui/components/push-switch";
import { Toaster } from "@car/ui/components/sonner";
import { TooltipProvider } from "@car/ui/components/tooltip";
import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { ThemeProvider } from "next-themes";
import { Suspense, lazy, useEffect, useState } from "react";

import { LiveChat } from "@/components/live-chat";
import { captureAttribution } from "@/lib/attribution";
import { useConsultOpen } from "@/lib/consult";
import { loadSiteContent } from "@/lib/content";
import { pageMeta, siteHref } from "@/lib/seo";
import { imageConfig, imageOrigin, uploadTransport } from "@/lib/uploads";
import type { orpc } from "@/utils/orpc";

import appCss from "../index.css?url";
export interface RouterAppContext {
  orpc: typeof orpc;
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
  // texts and images staff edit in the panel, before any page renders (useSiteContent)
  loader: ({ context }) => loadSiteContent(context.queryClient),
  // site-wide defaults; a page's own title, description and og:* replace them (deepest wins).
  // canonical: the shown page's path without its query, so /vehicles?fuel=… is /vehicles
  head: ({ loaderData, matches }) => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      ...pageMeta(loaderData?.seo.title ?? "تابان خودرو", loaderData?.seo.description),
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "تابان خودرو" },
      { property: "og:locale", content: "fa_IR" },
      { property: "og:url", content: siteHref(matches.at(-1)?.pathname ?? "/") },
      { property: "og:image", content: siteHref("/logo.png") },
      { name: "twitter:card", content: "summary_large_image" },
      // full-size photos next to a result (the catalog's car photos) and no snippet limit;
      // the private pages set their own noindex, and robots.txt keeps crawlers off them
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { name: "theme-color", content: "#FAFAFA" },
      // the home-screen name on iOS; the other platforms read the manifest
      { name: "apple-mobile-web-app-title", content: "تابان خودرو" },
      { name: "mobile-web-app-capable", content: "yes" },
    ],
    links: [
      // every car photo comes through the optimizer on the API host. Only the name is looked
      // up here, not a whole connection: a `preconnect` measured ~۱۷۰ms worse on LCP and Speed
      // Index, because the TLS handshake to that host landed in the middle of the first paint
      { rel: "dns-prefetch", href: imageOrigin },
      {
        rel: "canonical",
        href: siteHref(matches.at(-1)?.pathname ?? "/"),
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        type: "image/png",
        href: "/favicon.png",
      },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // installable app (public/manifest.webmanifest + public/sw.js)
      { rel: "manifest", href: "/manifest.webmanifest" },
    ],
  }),

  component: RootDocument,
});

const GA_ID = "G-FZGFF6KGZB";

declare global {
  interface Window {
    /** gtag.js reads what was queued here before it loaded */
    dataLayer?: unknown[];
  }
}

/**
 * Google Analytics (gtag.js). The tag is injected after the page has loaded and the browser
 * has gone idle, not from <head>: gtag.js is ~۱۷۰ کیلوبایت and spends ~نیم ثانیه on the main
 * thread, which in <head> lands right on top of hydration (Lighthouse: Total Blocking Time).
 * `load` first, then idle — an idle moment on its own can still fall inside the first paint.
 * The queue (dataLayer) is filled immediately, so the first page view carries the real
 * timestamp and only its delivery waits.
 */
/**
 * Runs `task` once the page has loaded and the main thread is idle — or after two seconds on a
 * page that never goes idle (Safari has no requestIdleCallback). `load` first, then idle: an
 * idle moment on its own can still fall inside the first paint. Returns a useEffect cleanup.
 */
function onIdleAfterLoad(task: () => void) {
  let handle = 0;
  const idle = "requestIdleCallback" in window;
  const afterLoad = () => {
    handle = idle
      ? window.requestIdleCallback(task, { timeout: 2000 })
      : window.setTimeout(task, 2000);
  };
  if (document.readyState === "complete") afterLoad();
  else window.addEventListener("load", afterLoad, { once: true });
  return () => {
    window.removeEventListener("load", afterLoad);
    if (!handle) return;
    if (idle) window.cancelIdleCallback(handle);
    else window.clearTimeout(handle);
  };
}

function useAnalytics() {
  useEffect(() => {
    window.dataLayer ??= [];
    // the real snippet pushes `arguments`; an array is array-like in the same way gtag reads it
    window.dataLayer.push(["js", new Date()], ["config", GA_ID]);
    return onIdleAfterLoad(() => {
      const script = document.createElement("script");
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      script.async = true;
      document.head.append(script);
    });
  }, []);
}

const ConsultDialog = lazy(() =>
  import("@/components/consult-dialog").then((module) => ({ default: module.ConsultDialog })),
);

/**
 * «مشاوره رایگان» و گفتگوی زنده: هیچ‌کدام تا کلیک کاربر دیده نمی‌شوند، ولی کدشان در چانکِ ورودی
 * بود و موقع باز شدن هر صفحه پارس و اجرا می‌شد (Total Blocking Time). حالا با import پویا و پس
 * از بیکار شدن مرورگر سوار می‌شوند. اگر کاربر زودتر «مشاوره» را بزند، همان لحظه می‌آید.
 */
function DeferredUi() {
  const [idle, setIdle] = useState(false);
  const consultOpen = useConsultOpen();
  useEffect(() => onIdleAfterLoad(() => setIdle(true)), []);
  if (!idle && !consultOpen) return null;
  return (
    <Suspense fallback={null}>
      {/* «مشاوره رایگان» هر صفحه همین یک دیالوگ را باز می‌کند (openConsult) */}
      <ConsultDialog />
      <LiveChat />
    </Suspense>
  );
}

function RootDocument() {
  // the campaign or site this visit came from, sent later with the consult forms
  useEffect(captureAttribution, []);
  useAnalytics();
  const router = useRouter();
  useEffect(() => registerServiceWorker(router.history), [router]);
  return (
    <html lang="fa" dir="rtl" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <ThemeProvider
          attribute="class"
          defaultTheme="light"
          enableSystem={false}
          storageKey="car-theme"
        >
          <TooltipProvider>
            <ImageConfigProvider value={imageConfig}>
              <UploadProvider transport={uploadTransport}>
                <Outlet />
                <DeferredUi />
              </UploadProvider>
            </ImageConfigProvider>
            <Toaster richColors />
          </TooltipProvider>
        </ThemeProvider>
        {/* dev only; hold Shift+Alt+Ctrl and click any element to open its source */}
        {import.meta.env.DEV && (
          <TanStackDevtools
            plugins={[
              { name: "Router", render: <TanStackRouterDevtoolsPanel /> },
              { name: "Query", render: <ReactQueryDevtoolsPanel /> },
            ]}
          />
        )}
        <Scripts />
      </body>
    </html>
  );
}
